import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, Loader2, DatabaseBackup, AlertTriangle } from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export function BackupPanel({ onRestored }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/backup", { responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "club-infinity-backup.json";
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup gerado! Guarde o arquivo em local seguro.");
    } catch { toast.error("Não foi possível gerar o backup."); }
    finally { setBusy(false); }
  };

  const restore = async () => {
    const file = pending; setPending(null);
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/restore?replace=1", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const n = Object.keys(r.data.report || {}).length;
      toast.success(`Backup restaurado (${n} tabelas).`);
      onRestored?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao restaurar o backup."); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  return (
    <div className="ci-card p-6 lg:col-span-2" data-testid="settings-backup">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center shrink-0"><DatabaseBackup className="w-5 h-5 text-cyan-300" /></div>
        <div>
          <h2 className="text-xl font-bold">Backup do site</h2>
          <p className="text-sm text-gray-400">Um clique gera um arquivo com todo o conteúdo: tutoriais, dispositivos, apps, FAQ, status, comunicados, configurações, imagens e PDFs.</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={download} disabled={busy} data-testid="backup-download-btn"
          className="ci-btn-gold inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold hover:brightness-105 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Gerar backup agora
        </button>
        <button onClick={() => ref.current?.click()} disabled={busy} data-testid="backup-restore-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border border-white/15 hover:bg-white/5 disabled:opacity-50">
          <Upload className="w-4 h-4" /> Restaurar de um backup
        </button>
        <input ref={ref} type="file" accept="application/json,.json" className="hidden" data-testid="backup-restore-input"
          onChange={(e) => e.target.files?.[0] && setPending(e.target.files[0])} />
      </div>
      <p className="mt-3 text-xs text-amber-200/80 inline-flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Restaurar substitui todo o conteúdo atual pelo conteúdo do arquivo.</p>
      <ConfirmDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)} onConfirm={restore}
        title="Restaurar este backup?" itemName={pending?.name} confirmLabel="Sim, restaurar"
        description="Todo o conteúdo atual do site será substituído pelo conteúdo do arquivo. Recomendamos gerar um backup antes." />
    </div>
  );
}
